// utils/Common.ts

export interface DtpLangSource {
  days: string[];
  daysShort: string[];
  daysMin: string[];
  months: string[];
  monthsShort: string[];
  today: string;
  clear: string;
  firstDay: number;
}

const Common = {
  // Low-level SHA-256 implementation (returns hex string)
  sha256(s: string): string {
    const chrsz = 8;
    const hexcase = 0;

    function safe_add(x: number, y: number): number {
      const lsw = (x & 0xffff) + (y & 0xffff);
      const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
      return (msw << 16) | (lsw & 0xffff);
    }

    function S(X: number, n: number): number {
      return (X >>> n) | (X << (32 - n));
    }

    function R(X: number, n: number): number {
      return X >>> n;
    }

    function Ch(x: number, y: number, z: number): number {
      return (x & y) ^ (~x & z);
    }

    function Maj(x: number, y: number, z: number): number {
      return (x & y) ^ (x & z) ^ (y & z);
    }

    function Sigma0256(x: number): number {
      return S(x, 2) ^ S(x, 13) ^ S(x, 22);
    }

    function Sigma1256(x: number): number {
      return S(x, 6) ^ S(x, 11) ^ S(x, 25);
    }

    function Gamma0256(x: number): number {
      return S(x, 7) ^ S(x, 18) ^ R(x, 3);
    }

    function Gamma1256(x: number): number {
      return S(x, 17) ^ S(x, 19) ^ R(x, 10);
    }

    // Array index accesses use `?? 0` instead of `!` to satisfy noNonNullAssertion lint rule.
    // All indices are algorithmically guaranteed to be in bounds; the fallback never triggers.
    function core_sha256(m: number[], l: number): number[] {
      const K = [
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4,
        0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe,
        0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0xfc19dc6, 0x240ca1cc, 0x2de92c6f,
        0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
        0xc6e00bf3, 0xd5a79147, 0x6ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc,
        0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
        0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116,
        0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
        0xc67178f2,
      ];

      const HASH = [
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab,
        0x5be0cd19,
      ];

      const W = new Array<number>(64);
      let a: any, b: any, c: any, d: any, e: any, f: any, g: any, h: any;
      let T1: any, T2: any;

      m[l >> 5] = (m[l >> 5] ?? 0) | (0x80 << (24 - (l % 32)));
      m[(((l + 64) >> 9) << 4) + 15] = l;

      for (let i = 0; i < m.length; i += 16) {
        a = HASH[0] ?? 0;
        b = HASH[1] ?? 0;
        c = HASH[2] ?? 0;
        d = HASH[3] ?? 0;
        e = HASH[4] ?? 0;
        f = HASH[5] ?? 0;
        g = HASH[6] ?? 0;
        h = HASH[7] ?? 0;

        for (let j = 0; j < 64; j++) {
          if (j < 16) W[j] = m[j + i] ?? 0;
          else
            W[j] = safe_add(
              safe_add(
                safe_add(Gamma1256(W[j - 2] ?? 0), W[j - 7] ?? 0),
                Gamma0256(W[j - 15] ?? 0)
              ),
              W[j - 16] ?? 0
            );

          T1 = safe_add(
            safe_add(safe_add(safe_add(h, Sigma1256(e)), Ch(e, f, g)), K[j] ?? 0),
            W[j] ?? 0
          );
          T2 = safe_add(Sigma0256(a), Maj(a, b, c));
          h = g;
          g = f;
          f = e;
          e = safe_add(d, T1);
          d = c;
          c = b;
          b = a;
          a = safe_add(T1, T2);
        }

        HASH[0] = safe_add(a, HASH[0] ?? 0);
        HASH[1] = safe_add(b, HASH[1] ?? 0);
        HASH[2] = safe_add(c, HASH[2] ?? 0);
        HASH[3] = safe_add(d, HASH[3] ?? 0);
        HASH[4] = safe_add(e, HASH[4] ?? 0);
        HASH[5] = safe_add(f, HASH[5] ?? 0);
        HASH[6] = safe_add(g, HASH[6] ?? 0);
        HASH[7] = safe_add(h, HASH[7] ?? 0);
      }
      return HASH;
    }

    function str2binb(str: string): number[] {
      const bin: number[] = [];
      const mask = (1 << chrsz) - 1;
      for (let i = 0; i < str.length * chrsz; i += chrsz) {
        bin[i >> 5] = (bin[i >> 5] ?? 0) | ((str.charCodeAt(i / chrsz) & mask) << (24 - (i % 32)));
      }
      return bin;
    }

    function Utf8Encode(string: string): string {
      string = string.replace(/\r\n/g, '\n');
      let utftext = '';

      for (let n = 0; n < string.length; n++) {
        const c = string.charCodeAt(n);

        if (c < 128) {
          utftext += String.fromCharCode(c);
        } else if (c > 127 && c < 2048) {
          utftext += String.fromCharCode((c >> 6) | 192);
          utftext += String.fromCharCode((c & 63) | 128);
        } else {
          utftext += String.fromCharCode((c >> 12) | 224);
          utftext += String.fromCharCode(((c >> 6) & 63) | 128);
          utftext += String.fromCharCode((c & 63) | 128);
        }
      }
      return utftext;
    }

    function binb2hex(binarray: number[]): string {
      const hex_tab = hexcase ? '0123456789ABCDEF' : '0123456789abcdef';
      let str = '';
      for (let i = 0; i < binarray.length * 4; i++) {
        str +=
          hex_tab.charAt(((binarray[i >> 2] ?? 0) >> ((3 - (i % 4)) * 8 + 4)) & 0xf) +
          hex_tab.charAt(((binarray[i >> 2] ?? 0) >> ((3 - (i % 4)) * 8)) & 0xf);
      }
      return str;
    }

    s = Utf8Encode(s);
    return binb2hex(core_sha256(str2binb(s), s.length * chrsz));
  },

  // Wrapper: adds a fixed salt to the password before hashing
  SHA256(password: string): string {
    const salt = '5f4dcc3b5aa765d61d8327deb882cf00';
    const saltedPassword = salt + password;
    return this.sha256(saltedPassword);
  },

  // Returns language source data for datepicker based on lang code
  getDTPLangSource(lang: 'eng' | 'ind' | 'kor'): DtpLangSource | undefined {
    const langData: Record<string, DtpLangSource> = {
      ENG: {
        days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        daysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        months: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ],
        monthsShort: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ],
        today: 'Today',
        clear: 'Clear',
        firstDay: 0,
      },
      IND: {
        days: ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'],
        daysShort: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'],
        daysMin: ['Mg', 'Sn', 'Sl', 'Rb', 'Km', 'Jm', 'Sb'],
        months: [
          'Januari',
          'Februari',
          'Maret',
          'April',
          'Mei',
          'Juni',
          'Juli',
          'Agustus',
          'September',
          'Oktober',
          'November',
          'Desember',
        ],
        monthsShort: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'Mei',
          'Jun',
          'Jul',
          'Agt',
          'Sep',
          'Okt',
          'Nov',
          'Des',
        ],
        today: 'Hari ini',
        clear: 'Hapus',
        firstDay: 0,
      },
      KOR: {
        days: ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
        daysShort: ['일', '월', '화', '수', '목', '금', '토'],
        daysMin: ['일', '월', '화', '수', '목', '금', '토'],
        months: [
          '1월',
          '2월',
          '3월',
          '4월',
          '5월',
          '6월',
          '7월',
          '8월',
          '9월',
          '10월',
          '11월',
          '12월',
        ],
        monthsShort: [
          '1월',
          '2월',
          '3월',
          '4월',
          '5월',
          '6월',
          '7월',
          '8월',
          '9월',
          '10월',
          '11월',
          '12월',
        ],
        today: '오늘',
        clear: '초기화',
        firstDay: 0,
      },
    };

    switch (lang) {
      case 'eng':
        return langData.ENG;
      case 'ind':
        return langData.IND;
      case 'kor':
        return langData.KOR;
    }
  },
};

export default Common;
